import React, { useEffect, useState } from "react";
import { NavLink } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import LoadingSpinner from "../../../components/LoadingSpinner";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faChevronRight,
  faChevronLeft,
  faSearch,
  faChevronDown,
} from "@fortawesome/free-solid-svg-icons";
import { faThumbsUp } from "@fortawesome/free-regular-svg-icons";
import {
  SearchEventsByParams,
  getAttendees,
} from "../../../services/EventsService";
import { likeAthlete } from "../../../services/AthleteService";
import { toggleFavouriteLocal } from "../../../store/slices/athleteSlice";
import { toast } from "react-toastify";
import { useAxiosInstance } from "../../../services/Interceptor";
import AthleteScore from "../../../components/athlete-scores/athlete-score";
import useAuthUser from "react-auth-kit/hooks/useAuthUser";
import { useResponsiveHeight } from "../../../hooks/useResponsiveHeight";

function UniversityCombine() {
  const apiInstance = useAxiosInstance();
  const dispatch = useDispatch();
  const likedOverrides = useSelector((state) => state.athletes.likedOverrides);
  const [athletes, setAthletes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [combines, setCombines] = useState([]);
  const [selectedCombineId, setSelectedCombineId] = useState(null);
  const [selectedAthlete, setSelectedAthlete] = useState(null);
  const authUser = useAuthUser();
  const { user = {} } = authUser;

  // Dropdown state for custom combine dropdown
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [dropdownSearch, setDropdownSearch] = useState("");

  const fetchCombines = async () => {
    setLoading(true);
    try {
      const response = await SearchEventsByParams(apiInstance, "");
      setCombines(response);
    } catch (error) {
      console.error("Error fetching combines:", error);
    } finally {
      setLoading(false);
    }
  };

  const { adjustedHeight } = useResponsiveHeight({
    mobileHeight: 340,
    desktopHeight: 480,
  });

  const fetchAthletes = async (eventId) => {
    setLoading(true);
    try {
      const attendees = await getAttendees(apiInstance, eventId);
      setAthletes(attendees);
    } catch (error) {
      console.error("Error fetching attendees:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCombines();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleCombineChange = (combineId) => {
    setSelectedCombineId(combineId);
    setDropdownOpen(false);
    setDropdownSearch("");
    if (combineId) {
      fetchAthletes(combineId);
    } else {
      setAthletes([]);
    }
  };

  const handleSearch = (e) => {
    setSearchQuery(e.target.value);
  };

  const handleAthleteClick = (athlete) => {
    setSelectedAthlete(athlete);
  };

  const isAthleteFavorite = (athlete) =>
    athlete.userId in likedOverrides
      ? likedOverrides[athlete.userId]
      : athlete.liked;

  const handleLikeClick = async (e, athlete) => {
    e.stopPropagation();
    const shouldLike = !isAthleteFavorite(athlete);
    dispatch(
      toggleFavouriteLocal({
        athleteUserId: athlete.userId,
        liked: shouldLike,
      }),
    );
    try {
      await likeAthlete(apiInstance, shouldLike, athlete.userId);
    } catch (err) {
      dispatch(
        toggleFavouriteLocal({
          athleteUserId: athlete.userId,
          liked: !shouldLike,
        }),
      );
      toast.error(`Error liking athlete`, { position: "top-center" });
    }
  };

  const backButtonClickEvent = () => setSelectedAthlete(null);

  // Filter combines for dropdown search
  const filteredCombines = combines.filter(
    (combine) =>
      combine.name.toLowerCase().includes(dropdownSearch.toLowerCase()) ||
      combine.city.toLowerCase().includes(dropdownSearch.toLowerCase()) ||
      combine.date.toLowerCase().includes(dropdownSearch.toLowerCase()),
  );

  // Filter athletes for search input
  const filteredAthletes = athletes.filter(
    (a) =>
      `${a.name} ${a.lastname}`
        .toLowerCase()
        .includes(searchQuery.toLowerCase()) ||
      (a.position &&
        a.position.toLowerCase().includes(searchQuery.toLowerCase())),
  );

  // Find selected combine for display
  const selectedCombine = combines.find((c) => c.id === selectedCombineId);

  return (
    <>
      <div className="flex h-full">
        <div className="flex-grow sm:pb-20 xl:pb-0 lg:pb-0">
          <div className="p-4 h-full bg-gray-100">
            <div className="flex flex-col xl:flex-row lg:flex-row gap-4 h-full">
              <div className="w-full lg:w-2/5 xl:w-2/5">
                <div className="bg-white shadow-lg p-4 rounded-lg border-gray-300 h-full">
                  <h2 className="text-xl mb-3 uppercase">Combine Results</h2>

                  <div className="w-full">
                    {loading ? (
                      <LoadingSpinner />
                    ) : (
                      <div className="relative w-full">
                        <button
                          type="button"
                          className="border border-gray-300 text-gray-900 text-md rounded-md focus:ring-blue-500 focus:border-blue-500 block w-full p-2.5 flex items-center justify-between bg-white"
                          onClick={() => setDropdownOpen((prev) => !prev)}
                        >
                          <span>
                            {selectedCombine
                              ? `${selectedCombine.date} - ${selectedCombine.name} - ${selectedCombine.city}`
                              : "Select an event"}
                          </span>
                          <FontAwesomeIcon
                            icon={faChevronDown}
                            className="ml-2"
                          />
                        </button>
                        {dropdownOpen && (
                          <div className="absolute z-10 w-full bg-white border border-gray-300 rounded-md mt-1 shadow-lg">
                            <div className="flex items-center px-2 py-2">
                              <FontAwesomeIcon
                                icon={faSearch}
                                className="text-gray-400 mr-2"
                              />
                              <input
                                type="text"
                                placeholder="Search event name..."
                                className="w-full pl-2 pr-2 py-1 border border-gray-300 rounded-md focus:outline-none focus:ring focus:border-blue-400"
                                value={dropdownSearch}
                                onChange={(e) =>
                                  setDropdownSearch(e.target.value)
                                }
                                autoFocus
                              />
                            </div>
                            <ul className="max-h-80 overflow-y-auto">
                              {filteredCombines.length === 0 && (
                                <li className="px-4 py-2 text-gray-500">
                                  No results found
                                </li>
                              )}
                              {filteredCombines.map((combine) => (
                                <li
                                  key={combine.id}
                                  className="px-4 py-2 hover:bg-blue-100 cursor-pointer w-full truncate"
                                  onClick={() =>
                                    handleCombineChange(combine.id)
                                  }
                                >
                                  {combine.date} - {combine.name} -{" "}
                                  {combine.city}
                                </li>
                              ))}
                            </ul>
                          </div>
                        )}
                      </div>
                    )}
                  </div>

                  <div className="pb-0 mt-5 ">
                    <h1 className="text-xl mb-5 uppercase">ATHLETES</h1>
                    <div className="mb-4 flex items-center w-full border border-gray-300 rounded-md overflow-hidden">
                      <input
                        className="w-full p-2 shadow-sm focus:outline-none"
                        type="text"
                        value={searchQuery}
                        onChange={handleSearch}
                        placeholder="Search athletes..."
                      />
                      <button
                        type="button"
                        className="basis-1/12 bg-white text-slate-500 flex items-center justify-center"
                      >
                        <FontAwesomeIcon icon={faSearch} size="md" />
                      </button>
                    </div>
                    {loading ? (
                      <LoadingSpinner />
                    ) : (
                      <>
                        <ul
                          className="list-none p-0 mt-4 overflow-y-auto pr-3 -mr-4"
                          style={{ height: `${adjustedHeight + 30}px` }}
                        >
                          {filteredAthletes.length > 0 ? (
                            filteredAthletes.map((athlete, index) => (
                              <li
                                key={index}
                                className="cursor-pointer p-2 hover:bg-gray-200 border-slate-500 border mb-4 rounded-md px-2 shrink-0"
                                onClick={() => handleAthleteClick(athlete)}
                              >
                                <div className="flex justify-between items-center">
                                  <div className="flex flex-row flex-1">
                                    <div className="my-auto rounded-md">
                                      <img
                                        alt="Athlete Name"
                                        src={
                                          athlete.profilePicture ||
                                          "/default-profile-img.png"
                                        }
                                        className="grow-0 shrink-0 rounded-md h-[40px] w-[40px]"
                                        height={40}
                                        width={40}
                                      />
                                    </div>
                                    <div className="ml-4">
                                      <p className="text-lg uppercase">
                                        {athlete.name} {athlete.lastname}
                                      </p>
                                      <p className="text-sm text-gray-700">
                                        {athlete.position}
                                      </p>
                                    </div>
                                  </div>

                                  <button
                                    className="mr-6 text-[#8E8E93]"
                                    onClick={(e) => handleLikeClick(e, athlete)}
                                  >
                                    <div
                                      className={`flex items-center justify-center h-[40px] w-[40px] rounded-full ${
                                        isAthleteFavorite(athlete)
                                          ? "bg-yellow-500"
                                          : "bg-gray-400"
                                      }`}
                                    >
                                      <FontAwesomeIcon
                                        icon={faThumbsUp}
                                        style={{ color: "#fff" }}
                                      />
                                    </div>
                                  </button>
                                  <div className="my-auto h-[40px] w-[40px] bg-blue-700 text-center text-white rounded-md hidden">
                                    <p className="my-auto text-base mt-2 ">
                                      {user.role === "athlete" ||
                                      user.role === "admin"
                                        ? athlete.trackittScore
                                        : athlete.positionScore}
                                    </p>
                                  </div>
                                  <button className="p-2 rounded ml-6">
                                    <FontAwesomeIcon
                                      icon={faChevronRight}
                                      className="text-2xl font-bold"
                                    />
                                  </button>
                                </div>
                              </li>
                            ))
                          ) : (
                            <div className="w-full p-2 mt-5 flex flex-col items-center">
                              <img
                                alt="No events that match your search"
                                src="/nodata.gif"
                                className="object-cover h-20 w-20 rounded-full border-gray-300 border"
                              />

                              <span className="mt-4 text-center text-md text-slate-600">
                                {" "}
                                There are no athletes that match your search
                              </span>
                            </div>
                          )}
                        </ul>
                      </>
                    )}
                  </div>
                </div>
              </div>

              {selectedAthlete && (
                <div className="w-full lg:w-3/5 xl:w-3/5">
                  <div className="athletes-list h-full event_details_mob md:relative xl:relative lg:relative  md:col-span-7 xl:col-span-7 xl:space-y-0 xl:bg-transparent  xl:border-gray-300 p-0  md:space-y-0 md:top-0 xl:top-0 lg:top-0">
                    <div className="text-blue-600 pt-6 px-6 md:visible xl:hidden lg:hidden sm:visible ">
                      <NavLink onClick={() => backButtonClickEvent(false)}>
                        <FontAwesomeIcon icon={faChevronLeft} /> Back
                      </NavLink>
                    </div>

                    {selectedAthlete ? (
                      <div className="overflow-x-scroll h-full mb-10">
                        <AthleteScore
                          userId={selectedAthlete.userId}
                          showOffers={true}
                          showTrackittScore={true}
                          showPositionScore={true}
                          searchEvent={false}
                          showProfile={true}
                          showRank={true}
                          showLikes={true}
                          eventId={selectedCombineId || undefined}
                        />
                      </div>
                    ) : (
                      <div className="flex items-center justify-center h-full">
                        <div className="w-full p-2 mt-5 flex flex-col items-center">
                          <img
                            alt="Select Athlete"
                            src="/select_row.svg"
                            className="object-cover h-20 ml-18 pb-3"
                          />
                          <p className="text-center text-[#8E8E93] text-1xl">
                            Please select an Athlete to view their Profile
                          </p>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

export default UniversityCombine;
